import './ShareProjectModal.scss';
import { Uuid } from '@belimo-retrofit-portal/logic';
import { FormController } from '@form-ts/core';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'src/modules/common/components/Button';
import { CustomLoading } from 'src/modules/common/components/CustomLoading';
import { ModalBody } from 'src/modules/common/components/ModalBody';
import { ModalFooter } from 'src/modules/common/components/ModalFooter';
import { ModalHeader } from 'src/modules/common/components/ModalHeader';
import { LinkIcon } from 'src/modules/common/icons/LinkIcon';
import { AuthUser } from 'src/modules/common/types/AuthUser';
import { Project } from 'src/modules/common/types/Project';
import { ProjectRole } from 'src/modules/common/types/ProjectRole';
import { FormError } from 'src/modules/form/types/FormError';
import { SharedUser } from 'src/modules/sharing-projects/types/SharedUser';
import { ShareProjectForm } from 'src/modules/sharing-projects/types/ShareProjectForm';
import { ShareProjectData } from 'src/modules/sharing-projects/types/ShareProjectState';
import { ShareProjectShareForm } from 'src/modules/sharing-projects/views/ShareProjectShareForm';
import { ShareProjectUserList } from 'src/modules/sharing-projects/views/ShareProjectUserList';
import { Language } from 'src/types/common/Language';

type Props = {
  readonly language: Language;
  readonly project: Project;
  readonly modalData: ShareProjectData;
  readonly loggedInUser: AuthUser;
  readonly onSubmit: (form: FormController<ShareProjectForm, FormError>) => void;
  readonly onCancel: () => void;
  readonly onRemove: (user: SharedUser) => void;
  readonly changeUserRole: (args: {
    userId: Uuid;
    role: ProjectRole;
  }) => void;
  readonly changeOwnRole: (args: {
    user: SharedUser;
    role: ProjectRole;
  }) => void;
  readonly changeProjectOwner: (user: SharedUser) => void;
  readonly copyLinkToProject: () => void;
};

export const ShareProjectModal = ({
  language,
  project,
  modalData,
  loggedInUser,
  onSubmit,
  onCancel,
  onRemove,
  changeUserRole,
  changeOwnRole,
  changeProjectOwner,
  copyLinkToProject,
}: Props): React.ReactElement => {
  const intl = useIntl();

  return (
    <>
      <ModalHeader
        title={<FormattedMessage id="shareProject/modal/label"/>}
        handleClose={onCancel}
      />
      <ModalBody>
        <div className="bp-share-project-modal__body">
          {modalData.sharedUsers.state === 'success' && modalData.organizationUsers.state === 'success' ? (
            <>
              <div className="bp-share-project-modal__form">
                <ShareProjectShareForm
                  project={project}
                  loggedInUser={loggedInUser}
                  sharedUsers={modalData.sharedUsers.value}
                  organizationUsers={modalData.organizationUsers.value}
                  onSubmit={onSubmit}
                />
              </div>

              <div className="bp-share-project-modal__users">
                <ShareProjectUserList
                  language={language}
                  project={project}
                  loggedInUser={loggedInUser}
                  users={modalData.sharedUsers.value}
                  onRemove={onRemove}
                  changeUserRole={changeUserRole}
                  changeOwnRole={changeOwnRole}
                  changeProjectOwner={changeProjectOwner}
                />
              </div>
            </>
          ) : (
            <div className="bp-share-project-modal__loading">
              <CustomLoading/>
            </div>
          )}
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          kind="secondary"
          className="bp-share-project-modal__finish-button"
          fullWidth={true}
          onClick={onCancel}
        >
          <FormattedMessage id="shareProject/modal/action/finish"/>
        </Button>
      </ModalFooter>
    </>
  );
};
